/**
 * Shared one-shot glitch primitives.
 *
 * singleHit — returns a GlitchChar[] snapshot where every non-space character
 * is replaced with a random glyph in the given color base. Feed into component
 * state and restore after N ms.
 *
 * @example
 *   setChars(singleHit(term, 'teal'))
 *   setTimeout(() => setChars(baseChars(term)), 100)
 *
 * Color bases:
 *   mixed  — all signal palette colors (default)
 *   luster — luster brand orange
 *   teal   — cool cyan-teal
 *   peach  — warm soft orange-peach
 *   red    — error / alert red
 *   violet — purple-violet
 *   amber  — warm amber
 */

export const GLYPHS = '!█▓▒░◆■□∅≠∆∇⊂⊃∞≈◎○●⟨⟩∈∉'

export type GlitchColorBase = 'mixed' | 'luster' | 'teal' | 'peach' | 'red' | 'violet' | 'amber'

export const COLOR_BASES: Record<GlitchColorBase, string[]> = {
  mixed: ['text-luster-400', 'text-teal-400', 'text-red-400', 'text-violet-400'],
  luster: ['text-luster-300', 'text-luster-400'],
  teal: ['text-teal-300', 'text-teal-400'],
  peach: ['text-orange-200', 'text-orange-300', 'text-amber-200'],
  red: ['text-red-300', 'text-red-400'],
  violet: ['text-violet-300', 'text-violet-400'],
  amber: ['text-amber-300', 'text-amber-400'],
}

export type GlitchChar = { char: string; col: string | null }

/** Returns the original characters as a neutral (no-glitch) snapshot. */
export function baseChars(term: string): GlitchChar[] {
  return term.split('').map(c => ({ char: c, col: null }))
}

/**
 * singleHit — one-shot glitch snapshot.
 * Spaces are preserved so layout never shifts on multi-word terms.
 * density: 0.0 to 1.0 (defaults to 1.0 for legacy behavior).
 */
export function singleHit(term: string, colorBase: GlitchColorBase = 'mixed', density = 1.0): GlitchChar[] {
  const cols = COLOR_BASES[colorBase]
  const mixed = COLOR_BASES.mixed

  return term.split('').map(char => {
    if (char === ' ') return { char: ' ', col: null }

    // If we're not hitting this char based on density, return neutral
    if (Math.random() > density) return { char, col: null }

    // Even if a specific colorBase is chosen, take a color from the global 
    // signal palette 30% of the time to restore multichromatic elegance.
    const colPalette = (colorBase !== 'mixed' && Math.random() < 0.3) ? mixed : cols

    // Even at high density, keep the original char 5% of the time (but colored) 
    // to create a "glimmer" effect that adds variation.
    const useGlyph = Math.random() > 0.05
    return {
      char: useGlyph ? GLYPHS[Math.floor(Math.random() * GLYPHS.length)] : char,
      col: colPalette[Math.floor(Math.random() * colPalette.length)],
    }
  })
}
